// Microsoft sign-in through Nexus's Keycloak (realm "nexus", whose only IdP is Kenbright's Entra tenant).
// PKCE authorization-code flow, ported from broker-workspace/src/auth/keycloak.js. Keycloak's token is used
// once — POST /auth/sso swaps it for the normal Nexus tokens — so there is no Keycloak refresh here.
import { newVerifier, challengeFor, newState } from './pkce';

const env = import.meta.env;

export const KC = {
    issuer: env.VITE_KC_ISSUER || '',
    clientId: env.VITE_KC_CLIENT_ID || 'nexus-admin',
    redirectUri: typeof window !== 'undefined' ? `${window.location.origin}/sso/callback` : '',
    postLogoutUri: typeof window !== 'undefined' ? `${window.location.origin}/login` : '',
};

const OIDC = {
    auth: `${KC.issuer}/protocol/openid-connect/auth`,
    token: `${KC.issuer}/protocol/openid-connect/token`,
    logout: `${KC.issuer}/protocol/openid-connect/logout`,
};

const SS = { verifier: 'nexus_kc_verifier', state: 'nexus_kc_state' };
// Kept only so "Sign out" can also end the Keycloak session
const ID_TOKEN = 'nexus_kc_id_token';

export const ssoEnabled = () => !!KC.issuer;

// Redirect to Keycloak, which goes straight on to Microsoft (kc_idp_hint).
export async function login() {
    const verifier = newVerifier();
    const state = newState();
    sessionStorage.setItem(SS.verifier, verifier);
    sessionStorage.setItem(SS.state, state);
    const params = new URLSearchParams({
        client_id: KC.clientId,
        response_type: 'code',
        scope: 'openid profile email',
        redirect_uri: KC.redirectUri,
        state,
        code_challenge: await challengeFor(verifier),
        code_challenge_method: 'S256',
        kc_idp_hint: 'microsoft',
    });
    window.location.assign(`${OIDC.auth}?${params}`);
}

// On /sso/callback: check state, exchange the code. Returns the Keycloak access token, or throws.
export async function handleCallback(search) {
    const q = new URLSearchParams(search);
    if (q.get('error')) {
        throw new Error(q.get('error_description') || q.get('error'));
    }
    const code = q.get('code');
    const state = q.get('state');
    const verifier = sessionStorage.getItem(SS.verifier);
    const expected = sessionStorage.getItem(SS.state);
    sessionStorage.removeItem(SS.verifier);
    sessionStorage.removeItem(SS.state);
    if (!code || !state || state !== expected || !verifier) {
        throw new Error('Sign-in response was not expected. Please try again.');
    }
    const res = await fetch(OIDC.token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: KC.clientId,
            code,
            redirect_uri: KC.redirectUri,
            code_verifier: verifier,
        }),
    });
    if (!res.ok) throw new Error(`Microsoft sign-in failed (${res.status})`);
    const tokens = await res.json();
    if (tokens.id_token) localStorage.setItem(ID_TOKEN, tokens.id_token);
    return tokens.access_token;
}

export const hasKeycloakSession = () => !!localStorage.getItem(ID_TOKEN);

export function forgetKeycloakSession() {
    localStorage.removeItem(ID_TOKEN);
}

// Ends the Keycloak session too, so the next "Sign in with Microsoft" asks again. Redirects to /login.
export function logout() {
    const idToken = localStorage.getItem(ID_TOKEN);
    localStorage.removeItem(ID_TOKEN);
    const params = new URLSearchParams({ client_id: KC.clientId, post_logout_redirect_uri: KC.postLogoutUri });
    if (idToken) params.set('id_token_hint', idToken);
    window.location.assign(`${OIDC.logout}?${params}`);
}
